import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Input, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload/*, faEye, faEyeSlash */} from '@fortawesome/free-solid-svg-icons'
import ArtistScoreBadge from './ArtistScoreBadge.js';
import MediaRanking from '../Media/MediaRanking.js'
import SetView from '../Sets/SetView.js'
import { DateTime } from 'luxon';
import ArtistTrackDownloadModal from './ArtistTrackDownloadModal.js';

const ArtistDetail = ({
    artistName,
    artistGUID,
    getArtists,
    soundcloudMedia,
    soundcloudUser,
    selectedArtists,
    onChangeSelectedArtists,
    sets,
    stages,
    eventSchedules,
    userWeightedScore,
    showLongSetMedia = true,
    showSongMedia = true,
    showTracks = false,
    onToggleShowTracks = () => {}
}) => {

    const [artistTracks, setArtistTracks] = useState([]);
    const [artistLongTracks, setArtistLongTracks] = useState([]);
    const [artistPersonalTracks, setArtistPersonalTracks] = useState([]);
    const [artistSearch, setArtistSearch] = useState([]);
    const [setInputs, setSetInputs] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [trackID, setTrackID] = useState('');

    const getAssociatedFollowers = (users = []) => {
        return users.reduce((maxFollowers, user) => {
            const followers = Number(
                user?.mediaMetaData?.followers ??
                user?.mediaMetaData?.followers_count ??
                0
            );
            if (!Number.isFinite(followers)) return maxFollowers;
            return Math.max(maxFollowers, followers);
        }, 0);
    };

    const formatFollowersK = (followers) => {
        const value = Number(followers || 0);
        if (!Number.isFinite(value) || value <= 0) return '';
        const thousands = value / 1000;
        const formatted = thousands >= 100
            ? Math.round(thousands).toString()
            : thousands.toFixed(1).replace(/\.0$/, '');
        return `${formatted}K`;
    };

    const getRankedMediaCount = (mediaList) => {
        if (!Array.isArray(mediaList)) return null;

        const total = mediaList.length;
        const ranked = mediaList.filter(m => m.userRankingRank != null).length;

        return `${ranked}/${total}`;
    };
    const filteredSoundcloudMedia = React.useMemo(() => {
        return (soundcloudMedia || []).filter((media) => {
            const isLongSet = Number(media.mediaLength || 0) >= (30 * 60 * 1000);
            if (isLongSet) return showLongSetMedia;
            return showSongMedia;
        });
    }, [soundcloudMedia, showLongSetMedia, showSongMedia]);
    const rankedMediaCount = getRankedMediaCount(filteredSoundcloudMedia) || '0/0';
    const [rankedVisibleCount, totalVisibleCount] = rankedMediaCount.split('/').map((value) => Number(value || 0));
    const associatedFollowersLabel = formatFollowersK(getAssociatedFollowers(soundcloudUser));

    const formatTimeForInput = (rawDatetime, eventTimezone = 'America/New_York') => {
        if (!rawDatetime) return '';

        const trimmed = rawDatetime.replace(/^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})\.(\d{3})\d{3}$/, '$1.$2');

        const dt = DateTime.fromFormat(trimmed, 'yyyy-MM-dd HH:mm:ss.SSS', { zone: eventTimezone });
        if (!dt.isValid) return ''; // fallback for safety

        return dt.setZone(eventTimezone).toFormat('HH:mm');
    };

    const getArtistTracks = () => {
        if (soundcloudUser.length > 0) {
            fetch(`/api/soundcloud/artist/tracks`, {
                method: 'POST',
                body: JSON.stringify({ artistId: soundcloudUser[0]?.mediaURL }),
                headers: { 'Content-Type': 'application/json' },
            })
                .then((response) => {
                    if (response.ok) { // Checks if the status is in the range 200-299
                        return response.json();
                    } else {
                        return null;
                    }
                })
                .then((json) => {
                    if (json) {
                        setArtistPersonalTracks(json.data/*.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))*/.slice(0, 10)); // Only set data if HTTP status is 200-299
                        setModalOpen(true);
                    }
                })
                .catch((error) => {
                });
        }
        fetch(`/api/soundcloud/artist`, {
            method: 'POST',
            body: JSON.stringify({ artist: artistName, minMinutes: 1, maxMinutes: 30 }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((json) => {
                if (json) {
                    setArtistTracks(json.data); // Only set data if HTTP status is 200-299
                    setModalOpen(true);
                }
            })
            .catch((error) => {
            });
        fetch(`/api/soundcloud/artist`, {
            method: 'POST',
            body: JSON.stringify({ artist: artistName + ' live', minMinutes: 30, maxMinutes: 180 }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((json) => {
                if (json) {
                    setArtistLongTracks(json.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 10)); // Only set data if HTTP status is 200-299
                    setModalOpen(true);
                }
            })
            .catch((error) => {
            });
        fetch(`/api/soundcloud/artist/search`, {
            method: 'POST',
            body: JSON.stringify({ artist: artistName }),
            headers: { 'Content-Type': 'application/json' },
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((json) => {
                if (json) {
                    setArtistSearch(json.data.sort((a, b) => parseInt(b.followers_count) - parseInt(a.followers_count)).slice(0, 10)); // Only set data if HTTP status is 200-299
                    setModalOpen(true);
                }
            })
            .catch((error) => {
            });
    };

    const getArtistTrackID = () => {
        fetch(`/api/soundcloud/track/${trackID}`, {
            method: 'GET',
        })
            .then((response) => {
                if (response.ok) { // Checks if the status is in the range 200-299
                    return response.json();
                } else {
                    return null;
                }
            })
            .then((json) => {
                if (json) {
                    if (json.data[0]?.duration < 1800000) {
                        setArtistTracks(json.data); // Only set data if HTTP status is 200-299
                        setArtistLongTracks([]);
                    } else {
                        setArtistTracks([]); // Only set data if HTTP status is 200-299
                        setArtistLongTracks(json.data); // Only set data if HTTP status is 200-299
                    }
                    setModalOpen(true);
                }
            })
            .catch((error) => {
            });
    };

    useEffect(() => {
        if (!sets || !Array.isArray(sets)) return;

        const initialInputs = {};
        sets.forEach(set => {
            initialInputs[set.setGUID] = {
                setEventScheduleGUID: set.setEventScheduleGUID,
                setStageGUID: set.setStageGUID,
                setStartDateTime: formatTimeForInput(set.setStartDateTime),
                setEndDateTime: formatTimeForInput(set.setEndDateTime),
                setB2BOrder: set.setB2BOrder ?? ''
            };
        });

        setSetInputs(initialInputs);
    }, [sets]);

    const val = s => s || ''; // null-safe

    return (
        <div className='artistRow'>
            <ArtistTrackDownloadModal
                artistName={artistName}
                artistGUID={artistGUID}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                artistTracks={artistTracks}
                setArtistTracks={setArtistTracks}
                artistPersonalTracks={artistPersonalTracks}
                setArtistPersonalTracks={setArtistPersonalTracks}
                artistLongTracks={artistLongTracks}
                setArtistLongTracks={setArtistLongTracks}
                artistSearch={artistSearch}
                setArtistSearch={setArtistSearch}
                soundcloudMedia={soundcloudMedia}
                soundcloudUser={soundcloudUser}
                getArtists={getArtists}
                trackID={trackID}
                setTrackID={setTrackID}
                getArtistTrackID={getArtistTrackID}
                getArtistTracks={getArtistTracks}
            />

            <Row className='mb-2 mt-0 py-2 border-top border-dark align-items-start'>
                <Col xs={7} className='pxx-1'>
                    <ArtistScoreBadge userWeightedScore={userWeightedScore} />
                    <Label for={artistGUID} className='ms-2'>
                        <h4>{artistName}</h4>
                    </Label>
                    {soundcloudUser.length > 0 && (
                        <>
                            <span className='ms-2'>&#9989;</span>
                            {associatedFollowersLabel && (
                                <span className='ms-1 text-secondary'>{associatedFollowersLabel}</span>
                            )}
                        </>
                    )}
                </Col>
                <Col className='d-flex justify-content-end text-right'>
                    <Input
                        className='me-2'
                        type="checkbox"
                        name={artistGUID}
                        id={artistGUID}
                        checked={selectedArtists.includes(artistGUID)}
                        onChange={(e) => onChangeSelectedArtists(e)}
                    />
                    <Button
                        className='me-2 py-1 px-2'
                        color="black"
                        onClick={() => {
                            if (artistTracks.length > 0 || artistLongTracks.length > 0) {
                                setModalOpen(true);
                            } else {
                                getArtistTracks();
                            }
                        }}>
                        <FontAwesomeIcon icon={faDownload} />
                    </Button>
                    {soundcloudMedia.length > 0 &&
                        <Button
                            className='py-0 px-3 me-2'
                            color={
                                totalVisibleCount === 0
                                    ? "secondary"
                                    : (rankedVisibleCount === totalVisibleCount ? "success" : "danger")
                            }
                            onClick={() => onToggleShowTracks(artistGUID)}
                        >
                            {rankedMediaCount}
                        </Button>}

                </Col>
            </Row>
            {!showTracks && <>
                {[...(sets || [])]
                    .sort((a, b) => val(a.eventScheduleStartDateTime).localeCompare(val(b.eventScheduleStartDateTime)))
                    .map(set => (
                        <Row className='mt-0'>
                            <Col className='px-2'>
                                <SetView
                                    key={set.setGUID}
                                    set={set}
                                    setInputs={setInputs}
                                    setSetInputs={setSetInputs}
                                    getArtists={getArtists}
                                    stages={stages}
                                    eventSchedules={eventSchedules}
                                />
                            </Col>
                        </Row>
                    ))}
            </>}
            {showTracks && <>
                <Row>
                    <Col md={12} className="pt-2 pxx-1">
                        {filteredSoundcloudMedia.length === 0 && (
                            <div className='text-secondary mb-2'>No media matches current filters.</div>
                        )}
                        {filteredSoundcloudMedia.map(media => (
                            <MediaRanking
                                key={media.mediaGUID}
                                media={media}
                                getArtists={getArtists}
                            />
                        ))}
                    </Col>
                </Row>
            </>
            }
        </div>
    );
}

export default ArtistDetail;
